import type React from 'react';
import { PulseLoader } from 'react-spinners';

import { getRecordValue } from '../../helpers/safeRecordAccess';

export interface ButtonProps {
  type?: 'submit' | 'reset' | 'button';
  children: React.ReactNode;
  variant?: 'primary' | 'solid' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
  onClick?: (e: React.FormEvent) => void;
  loading?: boolean;
  disabled?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  type,
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  onClick,
  loading = false,
  disabled,
}) => {
  const baseStyles =
    'font-body font-semibold rounded transition-all duration-200 flex items-center justify-center cursor-pointer';

  const variants = {
    primary:
      'bg-gradient-to-b from-accent-primary to-accent-hover text-surface-inverse hover:from-accent-hover hover:to-accent-primary disabled:opacity-50 disabled:cursor-not-allowed',
    solid:
      'bg-accent-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed',
    secondary:
      'bg-surface-card text-foreground-primary border border-border-subtle hover:bg-surface-primary disabled:opacity-50 disabled:cursor-not-allowed',
    outline:
      'border border-white/40 text-foreground-inverse bg-transparent hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed',
  };

  const sizes = {
    sm: 'px-6 py-2 text-sm',
    md: 'px-8 py-3 text-base',
    lg: 'px-10 py-[18px] text-base',
  };

  const spinnerColors = {
    primary: '#1a1a1a',
    solid: '#ffffff',
    secondary: '#1a1a1a',
    outline: '#ffffff',
  };

  const spinnerSizes = {
    sm: 8,
    md: 10,
    lg: 12,
  };

  return (
    <button
      type={type}
      className={`${baseStyles} ${getRecordValue(variants, variant)} ${getRecordValue(sizes, size)} ${fullWidth ? 'w-full' : ''} ${className}`}
      onClick={onClick}
      disabled={loading || disabled}
    >
      {loading ? (
        <PulseLoader
          color={getRecordValue(spinnerColors, variant)}
          size={getRecordValue(spinnerSizes, size)}
          margin={4}
        />
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
